/**
 * Player (phone) view for an active Scotland Yard game.
 *
 * Renders one of two role-specific UIs depending on whether the local `uid`
 * is Mr. X or a detective. The component:
 *  - shows the public board (via `InteractiveBoardCanvas`),
 *  - overlays per-role status (round, last-seen, your-turn badge),
 *  - lists the player's tickets,
 *  - enumerates legal moves via the pure engine (`legalMoves`) and renders
 *    one button per move; for Mr. X, also surfaces the "use black ticket"
 *    and "use double-move" toggles which expand/filter the move list.
 *
 * The component is render-pure aside from local UI state (toggles and the
 * "submitting" flag). Move submission is delegated to an optional
 * `onMove` callback provided by the platform's routing/service layer; if
 * absent, buttons render as visual-only placeholders so the view can be
 * exercised in storybook-style harnesses (App.tsx).
 *
 * Mr. X privacy: this component is the only place in the app that ever sees
 * `state.mrxStation`. Detective UIs receive the same prop but never read it
 * (`useMemo` short-circuits on `isMrX`). The platform layer is responsible
 * for ensuring detectives never receive the private subdoc in the first
 * place; we still write the code defensively.
 */

import { ActionIcon, Badge, Box, Button, Group, Paper, SimpleGrid, Stack, Switch, Text, Title } from '@mantine/core'
import { IconEye, IconEyeOff } from '@tabler/icons-react'
import { useCallback, useMemo, useState } from 'react'
import {
  SY_BOARD as boardData,
  PLAYER_COLOR_HEX,
  defaultAdjacency,
  legalMoves,
} from '@mdev/common'
import type { Room, Move, Board, GameState, LogTransport } from '@mdev/common'
import {
  IllegalMoveError,
  GameNotActiveError,
  RoomNotInGameError,
  MrxStateMissingError,
} from '../../lobby/errors'
import { applyMove as applySYMove } from '../service'
import { InteractiveBoardCanvas } from './InteractiveBoardCanvas'
import { SY } from '../theme'
import { TransportIcon } from './TransportIcon'
import { PositionChip } from './PositionChip'
import { stationColor } from './stationTransports'
import { formatTicketCount } from '../module'
import type { ScotlandYardSettings } from '../module'

/** Move-tile backgrounds — saturated so the white icon + number stay legible. */
const MOVE_BG: Record<LogTransport, string> = {
  taxi: '#F59E0B',
  bus: '#3B82F6',
  underground: '#EF4444',
  ferry: '#06B6D4',
  black: '#374151',
}

/**
 * Compact, transport-coloured destination tile. Laid out 3-up so a phone shows
 * many options without scrolling; white icon + station number for legibility.
 */
function MoveTile( {
  transport,
  toStation,
  move,
  onMove,
}: {
  transport: LogTransport
  toStation: number
  move: Move
  onMove: ( move: Move ) => Promise<void>
}) {
  const [ submitting, setSubmitting ] = useState( false )
  return (
    <Button
      fullWidth
      radius="md"
      loading={ submitting }
      styles={ {
        root: { background: MOVE_BG[ transport ], height: 58, paddingInline: 4 },
        label: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 },
      } }
      onClick={ async () => {
        setSubmitting( true )
        try {
          await onMove( move )
        } finally {
          setSubmitting( false )
        }
      } }
    >
      <TransportIcon transport={ transport } size={ 20 } color="#fff" />
      <Text
        span
        fw={ 800 }
        style={ {
          // Inner chip coloured by the DESTINATION station's type, so you see
          // both how you travel (tile) and what kind of stop you arrive at.
          background: stationColor( toStation ),
          color: '#fff',
          borderRadius: 999,
          border: '1.5px solid rgba(255,255,255,0.9)',
          padding: '0 7px',
          fontSize: 13,
          lineHeight: 1.6,
        } }
      >
        #{ toStation }
      </Text>
    </Button>
  )
}

/** Full-width fallback shown when a detective has no legal move. */
function StrandedButton( { uid, onMove }: { uid: string; onMove: ( move: Move ) => Promise<void> } ) {
  const [ submitting, setSubmitting ] = useState( false )
  return (
    <Button
      size="lg"
      variant="light"
      color="red"
      fullWidth
      loading={ submitting }
      onClick={ async () => {
        setSubmitting( true )
        try {
          await onMove( { kind: 'detective-stranded', uid } )
        } finally {
          setSubmitting( false )
        }
      } }
    >
      <Text c="bright" fw={ 700 }>Mark stranded</Text>
    </Button>
  )
}

const board = boardData as Board

interface Props {
  room: Room<ScotlandYardSettings>
  state: { public: GameState; mrxStation: number }
  uid: string
  /**
   * Optional override for move dispatch. Production callers leave this
   * unset; the component then dispatches via `applyMove` from
   * `games/scotland-yard/service.ts`. Dev harnesses (App.tsx /test) can
   * still inject a stub for visual-only previews.
   */
  onMove?: ( move: Move ) => Promise<void>
}

export function PlayerGameView( props: Props ) {
  const { room, state, uid, onMove } = props
  const isMrX = uid === state.public.mrx.uid
  const detective = state.public.detectives.find( ( d ) => d.uid === uid )

  const [ moveError, setMoveError ] = useState<string | null>( null )
  // Lets a player strip the pawns/labels off to study the bare map.
  const [ showMarkers, setShowMarkers ] = useState( true )

  // Default move dispatcher: write to Firestore via the SY service. Dev
  // harnesses can override via the `onMove` prop.
  const dispatchMove = useCallback(
    async ( move: Move ) => {
      setMoveError( null )
      try {
        if ( onMove ) {
          await onMove( move )
          return
        }
        await applySYMove( { code: room.code, move } )
      } catch ( err ) {
        // Friendly inline error display — no notifications lib in deps.
        if ( err instanceof IllegalMoveError ) {
          setMoveError( `Move rejected: ${ err.message }` )
        } else if ( err instanceof GameNotActiveError ) {
          setMoveError( 'The game has ended.' )
        } else if ( err instanceof RoomNotInGameError ) {
          setMoveError( 'Game state not found — try refreshing.' )
        } else if ( err instanceof MrxStateMissingError ) {
          setMoveError( 'Mr. X state missing — game data is corrupt.' )
        } else if ( err instanceof Error ) {
          setMoveError( err.message )
        } else {
          setMoveError( 'Unknown error submitting move.' )
        }
      }
    },
    [ onMove, room.code ],
  )

  if ( !isMrX && !detective ) {
    return (
      <Stack
        align="center"
        justify="center"
        h="100%"
        p="md"
        gap="xs"
        style={ { background: SY.pageBg } }
      >
        <Text
          c="bright"
          fw={ 600 }
        >
          Watching as a spectator
        </Text>
        <Text
          c="dimmed"
          size="sm"
          ta="center"
          maw={ 320 }
        >
          You weren’t seated in this game when it started. Open the room’s join
          link on this device to take a seat in the next game.
        </Text>
      </Stack>
    )
  }

  // Mr. X's view focuses on his real station; a detective focuses on theirs.
  const myStation = isMrX ? state.mrxStation : detective?.station ?? null
  const focusPoint = myStation != null ? findStationCoord( myStation ) : undefined

  return (
    <Stack
      gap={ 0 }
      h="100%"
      style={ { background: SY.pageBg, overflow: 'hidden' } }
    >
      <Box style={ { flex: '0 0 50%', minHeight: 0, position: 'relative' } }>
        <InteractiveBoardCanvas
          gameState={ state.public }
          focusOn={ focusPoint }
          initialZoom={ 2.2 }
          showMarkers={ showMarkers }
          mrxSelf={
            isMrX && state.mrxStation != null && state.mrxStation >= 0
              ? { station: state.mrxStation, avatar: state.public.mrx.avatar }
              : undefined
          }
        />
        <Box
          style={ {
            position: 'absolute',
            top: 12,
            left: 12,
            right: 12,
            display: 'flex',
            justifyContent: 'space-between',
            pointerEvents: 'none',
          } }
        >
          <Badge
            color="dark"
            variant="filled"
            size="lg"
          >
            Round { state.public.round } / 24
          </Badge>
          { state.public.mrx.lastRevealedStation != null && (
            <Badge
              color="yellow"
              variant="filled"
              size="lg"
            >
              Mr. X: #{ state.public.mrx.lastRevealedStation }
            </Badge>
          ) }
        </Box>
        <ActionIcon
          variant="filled"
          color="dark"
          size="xl"
          radius="xl"
          aria-label={ showMarkers ? 'Hide markers to study the map' : 'Show markers' }
          onClick={ () => setShowMarkers( ( v ) => !v ) }
          style={ { position: 'absolute', bottom: 12, right: 12, opacity: 0.92 } }
        >
          { showMarkers ? <IconEyeOff size={ 22 } /> : <IconEye size={ 22 } /> }
        </ActionIcon>
      </Box>

      <DetectivePositionsBar state={ state.public } />

      { isMrX ? (
        <MrxControls
          state={ state.public }
          mrxStation={ state.mrxStation }
          uid={ uid }
          onMove={ dispatchMove }
          moveError={ moveError }
        />
      ) : (
        <DetectiveControls
          state={ state.public }
          mrxStation={ state.mrxStation }
          uid={ uid }
          onMove={ dispatchMove }
          moveError={ moveError }
        />
      ) }
    </Stack>
  )
}

/**
 * A horizontal strip of every detective's current position, shown under the
 * map so any player can reference where the detectives are (mirrors the host
 * screen). Public info — safe for Mr. X to see too.
 */
function DetectivePositionsBar( { state }: { state: GameState } ) {
  if ( state.detectives.length === 0 ) return null
  return (
    <Box
      style={ {
        flex: '0 0 auto',
        background: SY.surfaceBg,
        borderTop: `1px solid ${ SY.border }`,
        borderBottom: `1px solid ${ SY.border }`,
        overflowX: 'auto',
      } }
    >
      <Group
        gap="lg"
        wrap="nowrap"
        p="xs"
        px="md"
      >
        { state.detectives.map( ( d ) => (
          <Group
            key={ d.uid }
            gap={ 6 }
            wrap="nowrap"
            align="center"
            style={ { flexShrink: 0, opacity: d.stranded ? 0.45 : 1 } }
          >
            <Text style={ { fontSize: 18, lineHeight: 1 } }>{ d.avatar }</Text>
            { d.stranded ? (
              <Text size="xs" c="dimmed">stranded</Text>
            ) : (
              <PositionChip
                station={ d.station }
                size="sm"
              />
            ) }
          </Group>
        ) ) }
      </Group>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Detective
// ---------------------------------------------------------------------------

interface RoleControlsProps {
  state: GameState
  mrxStation: number
  uid: string
  onMove: ( move: Move ) => Promise<void>
  moveError: string | null
}

function DetectiveControls( { state, mrxStation, uid, onMove, moveError }: RoleControlsProps ) {
  const me = state.detectives.find( ( d ) => d.uid === uid )!
  const isMyTurn =
    state.status === 'active' && state.phase === 'detectives' && state.awaitingUids.includes( uid )

  const moves = useMemo( () => {
    if ( !isMyTurn ) return []
    // Detective `legalMoves` doesn't actually need mrxStation, but engine
    // requires the param for Mr. X. We pass it through for parity even
    // though the detective branch ignores it.
    return legalMoves( state, uid, mrxStation, defaultAdjacency() )
  }, [ state, uid, mrxStation, isMyTurn ] )

  return (
    <Stack
      flex={ 1 }
      gap="xs"
      p="md"
      style={ { background: SY.panelBg, minHeight: 0, overflow: 'auto' } }
    >
      <Header
        avatar={ me.avatar }
        displayName={ me.displayName }
        colorHex={ PLAYER_COLOR_HEX[ me.color ] }
        station={ me.stranded ? null : me.station }
        note={ me.stranded ? 'Stranded' : undefined }
        isMyTurn={ isMyTurn }
        offTurnLabel={ offTurnLabel( state ) }
      />

      <Paper
        p="xs"
        radius="md"
        style={ { background: SY.surfaceBg, border: `1px solid ${ SY.border }` } }
      >
        <Group justify="space-around">
          <TicketCount transport="taxi" count={ me.tickets.taxi } />
          <TicketCount transport="bus" count={ me.tickets.bus } />
          <TicketCount transport="underground" count={ me.tickets.underground } />
        </Group>
      </Paper>

      <Title order={ 6 } c="dimmed" mt="xs">
        { isMyTurn
          ? moves.length === 0
            ? 'No legal moves — you will be stranded'
            : 'Choose a destination'
          : 'Legal moves will appear when it\'s your turn' }
      </Title>

      { isMyTurn && moves.length > 0 && (
        <SimpleGrid cols={ 3 } spacing="xs">
          { moves.map( ( move, i ) =>
            move.kind === 'detective' ? (
              <MoveTile
                key={ moveKey( move, i ) }
                transport={ move.transport }
                toStation={ move.toStation }
                move={ move }
                onMove={ onMove }
              />
            ) : null,
          ) }
        </SimpleGrid>
      ) }

      { isMyTurn && moves.length === 0 && (
        <StrandedButton uid={ uid } onMove={ onMove } />
      ) }

      { moveError && (
        <Paper
          p="xs"
          radius="md"
          style={ { background: SY.dangerBg, border: `1px solid ${ SY.dangerBorder }` } }
        >
          <Text size="sm" c="bright">{ moveError }</Text>
        </Paper>
      ) }
    </Stack>
  )
}

// ---------------------------------------------------------------------------
// Mr. X
// ---------------------------------------------------------------------------

function MrxControls( { state, mrxStation, uid, onMove, moveError }: RoleControlsProps ) {
  const mrx = state.mrx
  const isMyTurn =
    state.status === 'active' && state.phase === 'mrx' && state.awaitingUids.includes( uid )

  const [ useDoubleMove, setUseDoubleMove ] = useState( false )
  const [ preferBlackTicket, setPreferBlackTicket ] = useState( false )

  // Effective toggle: ignore a stale "on" switch once the cards are gone, so
  // running out of double-moves mid-game can't lock Mr. X out of single moves.
  const canDoubleMove = mrx.doubleMoves > 0
  const useDoubleMoveEffective = useDoubleMove && canDoubleMove

  const allMoves = useMemo( () => {
    if ( !isMyTurn ) return []
    return legalMoves( state, uid, mrxStation, defaultAdjacency() )
  }, [ state, uid, mrxStation, isMyTurn ] )

  // Filter the engine-emitted moves down to the ones matching the player's
  // current toggle state. The engine emits both black-ticket and natural-
  // ticket variants of every reachable destination; the toggles here let
  // Mr. X pick which flavour to commit to.
  const filteredMoves = useMemo( () => {
    if ( useDoubleMoveEffective ) {
      return allMoves.filter( ( m ) => m.kind === 'mrx-double' )
    }
    return allMoves.filter( ( m ) => {
      if ( m.kind !== 'mrx' ) return false
      if ( preferBlackTicket && mrx.blackTickets > 0 ) return m.useBlackTicket
      // Default: hide black-ticket variants unless explicitly requested.
      return !m.useBlackTicket || m.transport === 'ferry'
    } )
  }, [ allMoves, useDoubleMoveEffective, preferBlackTicket, mrx.blackTickets ] )

  return (
    <Stack
      flex={ 1 }
      gap="xs"
      p="md"
      style={ { background: SY.panelBg, minHeight: 0, overflow: 'auto' } }
    >
      <Header
        avatar={ mrx.avatar }
        displayName={ mrx.displayName }
        colorHex={ SY.border }
        station={ mrxStation }
        note="private"
        isMyTurn={ isMyTurn }
        offTurnLabel={ offTurnLabel( state ) }
      />

      <Paper
        p="xs"
        radius="md"
        style={ { background: SY.surfaceBg, border: `1px solid ${ SY.border }` } }
      >
        <Group justify="space-around">
          <Box>
            <Text size="xs" c="dimmed" ta="center">Black</Text>
            <Text c="bright" fw={ 700 } ta="center">{ mrx.blackTickets }</Text>
          </Box>
          <Box>
            <Text size="xs" c="dimmed" ta="center">Double</Text>
            <Text c="bright" fw={ 700 } ta="center">{ mrx.doubleMoves }</Text>
          </Box>
        </Group>
      </Paper>

      { isMyTurn && (
        <Stack gap="xs">
          <Switch
            label="Use black ticket"
            checked={ preferBlackTicket && mrx.blackTickets > 0 }
            onChange={ ( e ) => setPreferBlackTicket( e.currentTarget.checked ) }
            disabled={ mrx.blackTickets === 0 || useDoubleMoveEffective }
          />
          <Switch
            label="Play double-move"
            checked={ useDoubleMoveEffective }
            onChange={ ( e ) => setUseDoubleMove( e.currentTarget.checked ) }
            disabled={ !canDoubleMove }
          />
        </Stack>
      ) }

      <Title order={ 6 } c="dimmed" mt="xs">
        { isMyTurn
          ? filteredMoves.length === 0
            ? 'No legal moves with these options'
            : 'Choose a destination'
          : 'Waiting for detectives to move' }
      </Title>

      { isMyTurn && ( useDoubleMoveEffective ? (
        <Stack gap="xs">
          { filteredMoves.map( ( move, i ) =>
            move.kind === 'mrx-double' ? (
              <MrxDoubleButton
                key={ moveKey( move, i ) }
                move={ move }
                onMove={ onMove }
              />
            ) : null,
          ) }
        </Stack>
      ) : (
        <SimpleGrid cols={ 3 } spacing="xs">
          { filteredMoves.map( ( move, i ) =>
            move.kind === 'mrx' ? (
              <MoveTile
                key={ moveKey( move, i ) }
                transport={ move.useBlackTicket ? 'black' : move.transport }
                toStation={ move.toStation }
                move={ move }
                onMove={ onMove }
              />
            ) : null,
          ) }
        </SimpleGrid>
      ) ) }

      { moveError && (
        <Paper
          p="xs"
          radius="md"
          style={ { background: SY.dangerBg, border: `1px solid ${ SY.dangerBorder }` } }
        >
          <Text size="sm" c="bright">{ moveError }</Text>
        </Paper>
      ) }
    </Stack>
  )
}

function MrxDoubleButton( {
  move,
  onMove,
}: {
  move: Extract<Move, { kind: 'mrx-double' }>
  onMove: ( move: Move ) => Promise<void>
}) {
  const [ submitting, setSubmitting ] = useState( false )
  const a = move.first
  const b = move.second
  return (
    <Button
      size="lg"
      variant="light"
      color="grape"
      fullWidth
      justify="space-between"
      loading={ submitting }
      onClick={ async () => {
        setSubmitting( true )
        try {
          await onMove( move )
        } finally {
          setSubmitting( false )
        }
      } }
    >
      <Text c="bright" fw={ 700 }>
        2× → #{ a.toStation }{ a.useBlackTicket ? ' (black)' : '' } → #{ b.toStation }{ b.useBlackTicket ? ' (black)' : '' }
      </Text>
    </Button>
  )
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function Header( props: {
  avatar: string
  displayName: string
  colorHex: string
  /** Current station for the position chip (null when stranded/unknown). */
  station: number | null
  /** Small status note shown below (e.g. "private", "Stranded"). */
  note?: string
  isMyTurn: boolean
  offTurnLabel: string
}) {
  return (
    <Group justify="space-between" align="center">
      <Group gap="xs">
        <Box
          style={ {
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: props.colorHex,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 26,
          } }
        >
          { props.avatar }
        </Box>
        <Stack gap={ 2 }>
          <Text c="bright" fw={ 600 }>
            { props.displayName }
          </Text>
          { props.station != null ? (
            <Group gap={ 6 } align="center">
              <PositionChip
                station={ props.station }
                size="sm"
              />
              { props.note && (
                <Text size="xs" c="dimmed">{ props.note }</Text>
              ) }
            </Group>
          ) : (
            props.note && (
              <Text size="xs" c="dimmed">{ props.note }</Text>
            )
          ) }
        </Stack>
      </Group>
      { props.isMyTurn ? (
        <Badge color="yellow" size="lg" variant="filled">Your turn</Badge>
      ) : (
        <Badge color="gray" size="lg" variant="light">{ props.offTurnLabel }</Badge>
      ) }
    </Group>
  )
}

function offTurnLabel( state: GameState ): string {
  if ( state.status === 'detectives_won' ) return 'Detectives won'
  if ( state.status === 'mrx_won' ) return 'Mr. X won'
  if ( state.phase === 'mrx' ) return 'Mr. X is moving'
  const awaitingNames = state.detectives
    .filter( ( d ) => state.awaitingUids.includes( d.uid ) )
    .map( ( d ) => d.displayName )
  if ( awaitingNames.length === 0 ) return 'Waiting…'
  return `Waiting on ${ awaitingNames.join( ', ' ) }`
}

function TicketCount( {
  transport,
  count,
}: {
  transport: 'taxi' | 'bus' | 'underground'
  count: number
}) {
  return (
    <Stack gap={ 2 } align="center">
      <TransportIcon transport={ transport } size={ 22 } />
      <Text size="sm" c={ count === 0 ? 'red.5' : 'bright' } fw={ 700 }>
        { formatTicketCount( count ) }
      </Text>
    </Stack>
  )
}

function findStationCoord( id: number ): { x: number; y: number } | undefined {
  const s = board.stations.find( ( s ) => s.id === id )
  return s ? { x: s.x, y: s.y } : undefined
}

/**
 * Stable list key for a Move. Engine returns moves deterministically per call
 * but the index is also threaded in to disambiguate the (rare) case where
 * two moves stringify identically (e.g. both natural and black-ticket variants
 * of the same destination — only one would survive a content-based key).
 */
function moveKey( move: Move, index: number ): string {
  switch ( move.kind ) {
    case 'detective':
      return `${ index }:det:${ move.toStation }:${ move.transport }`
    case 'mrx':
      return `${ index }:mrx:${ move.toStation }:${ move.transport }:${ move.useBlackTicket ? 'b' : 'n' }`
    case 'mrx-double':
      return `${ index }:mrx2:${ move.first.toStation }:${ move.second.toStation }`
    case 'detective-stranded':
      return `${ index }:strand:${ move.uid }`
  }
}
